import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { signOut } from "../lib/auth";

/**
 * UserAvatarMenu — rev 23 Loop A0-auth-nav.
 *
 * Right-side navbar element when the user has a Cognito session.
 * Renders a 32×32 violet circle with the user's initials; click
 * opens a dropdown anchored bottom-right with email header + Portal
 * link + Sign-out button. Outside-click + Escape close.
 *
 * The PORTAL pill from rev-21 B4 stays alongside this avatar — owner
 * explicitly wants top-level navigation to /portal in the navbar,
 * not just buried in the dropdown.
 */

export interface UserAvatarMenuMe {
  /** Display name or email — first 2 letters become the avatar initials. */
  label: string;
}

export default function UserAvatarMenu({ me }: { me: UserAvatarMenuMe }) {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    // Focus first menuitem when opening for keyboard nav.
    setTimeout(() => {
      menuRef.current?.querySelector<HTMLElement>('[role="menuitem"]')?.focus();
    }, 0);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const initials = me.label
    .split(/[\s@.]/)
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s[0]?.toUpperCase() ?? "")
    .join("") || "?";

  const handleSignOut = async () => {
    try {
      await signOut();
    } finally {
      // Hard-nav so any cached auth state in components/hooks resets.
      window.location.assign("/");
    }
  };

  return (
    <div ref={wrapperRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Open user menu"
        data-user-avatar-trigger
        className="flex h-8 w-8 items-center justify-center rounded-full bg-nx-accent text-xs font-semibold uppercase text-white transition-shadow hover:shadow-[0_0_0_2px_rgba(107,79,230,0.4)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-nx-accent"
      >
        {initials}
      </button>
      {open ? (
        <div
          ref={menuRef}
          role="menu"
          aria-label="User menu"
          data-user-avatar-menu
          className="absolute right-0 top-10 z-40 min-w-[220px] rounded-nx2 border border-nx-line bg-nx-bg p-1 shadow-[0_8px_24px_rgba(0,0,0,0.4)]"
        >
          <div
            className="truncate px-3 py-2 text-[11px] uppercase tracking-[0.14em] text-nx-text-dim"
            title={me.label}
          >
            {me.label}
          </div>
          <div className="my-1 h-px bg-nx-line/60" />
          <Link
            to="/portal"
            role="menuitem"
            tabIndex={0}
            onClick={() => setOpen(false)}
            className="block rounded-nx1 px-3 py-2 text-sm text-nx-text hover:bg-nx-surface focus:bg-nx-surface focus:outline-none"
          >
            Portal
          </Link>
          <div className="my-1 h-px bg-nx-line/60" />
          <button
            type="button"
            role="menuitem"
            onClick={handleSignOut}
            data-user-avatar-signout
            className="block w-full rounded-nx1 px-3 py-2 text-left text-sm text-nx-text hover:bg-nx-surface focus:bg-nx-surface focus:outline-none"
          >
            Sign out
          </button>
        </div>
      ) : null}
    </div>
  );
}
